import React, { useLayoutEffect, useRef, useState } from "react";
import { Clock, Mic, ChevronDown, ChevronUp, Lock, Headphones } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import getLocalStorageData from '../../../utlis/storageUtils';
import Rabbit from "rabbit-node/lib/rabbit";


import styles from "./Card.module.css";
import { useNavbar } from "../../../context/NavBarContext";

// Constants
const SUMMARY_PREVIEW_LENGTH = 200;
const PURCHASE_API_URL = 'http://apiv2.shwenarsin.com/api/purchase/series';

// Custom Hooks
const useBookActions = (bookData) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleListen = (chaptersRef) => {
    chaptersRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleBookClick = () => {
    if (bookData.book_status !== 1) return;
    
    setLoading(true);
    setTimeout(() => {
      window.location.href = bookData.book_link;
    }, 1000);
  };

  const handleEpisodeClick = (episodeId) => {
    navigate(`/audio/book-detail/${episodeId}`);
  };

  return {
    loading,
    setLoading,
    handleListen,
    handleBookClick,
    handleEpisodeClick,
  };
};

const usePurchase = (bookData) => {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const purchaseData = {
    udid: "56007dc6d4747d53",
    series_id: bookData.series_id,
    phone:  getLocalStorageData(), //"959770179977",
    user_id: getLocalStorageData() //"959770179977"
  };

  const handlePurchase = () => setShowConfirm(true);

  const confirmPurchase = async () => {
    setShowConfirm(false);
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await fetch(PURCHASE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(purchaseData)
      });

      const result = await response.json();
      
      if (result.status === 2) {
        setErrorMessage(result.message);
      } else if (result.status === 1 || response.ok) {
        setSuccessMessage(result.message);
      } else {
        setErrorMessage(result.message || 'Purchase failed. Please try again.');
      }
    } catch (error) {
      console.error('Purchase API error:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelPurchase = () => {
    setShowConfirm(false);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const navigateToCreditFill = () => navigate("/credit-fill");

  const handleSuccessPurchase = () => {
    navigate(`/audio/book-series/${bookData.series_id}`, { replace: true });
    window.location.reload();
  };

  return {
    showConfirm,
    errorMessage,
    successMessage,
    loading,
    handlePurchase,
    confirmPurchase,
    cancelPurchase,
    navigateToCreditFill,
    handleSuccessPurchase,
  };
};

// UI Components
const LoadingSpinner = () => (
  <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex items-center justify-center z-50">
    <div
      className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-yellow-500 border-t-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
      role="status"
    >
      <span className="sr-only">Loading...</span>
    </div>
  </div>
);

const Modal = ({ children, isOpen }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="bg-white p-6 shadow-lg mx-5 text-center rounded-lg">
        {children}
      </div>
    </div>
  );
};

const ConfirmationModal = ({ onConfirm, onCancel, message }) => (
  <>
    <p className="mb-4 text-gray-900 text-sm font-medium">{message}</p>
    <div className="flex justify-center gap-4">
      <button
        onClick={onCancel}
        className="px-5 py-2 text-sm bg-gray-300 hover:bg-gray-400 rounded-2xl transition-colors"
      >
        No
      </button>
      <button
        onClick={onConfirm}
        className="px-5 py-2 text-sm bg-yellow-500 hover:bg-yellow-600 text-white rounded-2xl transition-colors"
      >
        Yes
      </button>
    </div>
  </>
);

const MessageModal = ({ message, onCancel, onConfirm, confirmText = "OK", cancelText = "Cancel" }) => (
  <>
    <p className="mb-4 text-gray-900 text-sm">{Rabbit.zg2uni(message)}</p>
    <div className="flex justify-center gap-4">
      <button
        onClick={onCancel}
        className="px-5 py-2 text-sm bg-gray-300 hover:bg-gray-400 rounded-2xl transition-colors"
      >
        {cancelText}
      </button>
      <button
        onClick={onCancel}  //onConfirm
        className="px-5 py-2 text-sm bg-yellow-500 hover:bg-yellow-600 text-white rounded-2xl transition-colors"
      >
        {confirmText}
      </button>
    </div>
  </>
);

const BookCover = ({ bookData }) => (
  <div className="relative">
    <div className="w-full h-64 flex items-center justify-center">
      <img 
        src={bookData.image} 
        alt={bookData.title}
        className="w-40 h-60 object-cover rounded-lg shadow-xl"
        onError={(e) => {
          e.target.src = '/placeholder-book-cover.jpg';
        }}
      />
    </div>
  </div>
);

const BookInfo = ({ bookData }) => (
  <div className="py-4 text-center">
    <h1 className="text-md font-bold mb-2">{bookData.title}</h1>
    <p className="text-gray-300 text-sm mb-1">
      စာရေးသူ <span className="text-blue-500 text-xl"> • </span>{bookData.author}
    </p>
    {bookData.buy === 1 && (
      <p className="text-white text-sm">All Episodes: {bookData.price} Kyats</p>
    )}
  </div>
);

const ActionButtons = ({ bookData, onListen, onPurchase, onBookClick, loading }) => {
  const renderButtons = () => {
    if (bookData.already_buy === 1) {
      return (
        <>
          <button
            onClick={onListen}
            className="w-1/2 flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-gray-800 py-2 rounded-full font-medium transition-colors"
            disabled={loading}
          >
            <Headphones size={20} />
            Listen
          </button>
          <div className="w-1/2 bg-gray-500 text-gray-200 py-2 rounded-full text-sm font-medium text-center flex items-center justify-center">
            ဝယ်ယူပြီးဖြစ်ပါသည်
          </div>
        </>
      );
    }

    const listenButton = (
      <button
        onClick={onListen}
        className={`${
          bookData.book_status === 1 || bookData.buy === 1 ? "w-1/2" : "w-full"
        } flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-gray-800 py-2 rounded-full font-medium transition-colors`}
        disabled={loading}
      >
        <Headphones size={20} />
        Listen
      </button>
    );

    if (bookData.buy === 1) {
      return (
        <>
          {listenButton}
          <button
            onClick={onPurchase}
            className="w-1/2 bg-yellow-500 hover:bg-yellow-600 text-gray-800 py-2 rounded-full font-medium transition-colors"
            disabled={loading}
          >
            Buy Now
          </button>
        </>
      );
    }

    if (bookData.book_status === 1) {
      return (
        <>
          {listenButton}
          <button
            onClick={onBookClick}
            className="w-1/2 bg-yellow-500 hover:bg-yellow-600 text-gray-800 py-2 rounded-full font-medium transition-colors"
            disabled={loading}
          >
            Read
          </button>
        </>
      );
    }

    return listenButton;
  };

  return <div className="flex gap-3 mb-4">{renderButtons()}</div>;
};

const BookStats = ({ bookData }) => (
  <div className="bg-gray-900 rounded-lg p-6 max-w-md mx-auto mb-4">
    <div className="flex divide-x divide-gray-600">
      <div className="flex-1">
        <div className="text-white flex justify-center font-semibold text-lg mb-3">
          Duration
        </div>
        <div className="flex items-center justify-center">
          <Clock className="w-5 h-5 mr-3 text-gray-400" />
          <span className="text-white text-sm font-mono">{bookData.duration}</span>
        </div>
      </div>
      <div className="flex-1 pl-6">
        <div className="text-white flex justify-center font-semibold text-lg mb-3">
          Narrator
        </div>
        <div className="flex items-center">
          <Mic className="w-5 h-5 mr-3 text-gray-400" />
          <span className="text-white text-sm">{bookData.readerName}</span>
        </div>
      </div>
    </div>
  </div>
);

const Summary = ({ bookData }) => {
  const [showFullSummary, setShowFullSummary] = useState(false);
  
  if (!bookData.summary) return null;

  const truncatedSummary = bookData.summary.length > SUMMARY_PREVIEW_LENGTH 
    ? `${bookData.summary.substring(0, SUMMARY_PREVIEW_LENGTH)}...`
    : bookData.summary;

  return (
    <div className="mb-4">
      <h2 className="text-yellow-400 text-lg font-bold mb-2">Summary</h2>
      <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
        {showFullSummary ? bookData.summary : truncatedSummary}
      </p>
      {bookData.summary.length > SUMMARY_PREVIEW_LENGTH && (
        <button 
          onClick={() => setShowFullSummary(!showFullSummary)}
          className="flex items-center mt-2 text-gray-400 text-sm hover:text-white transition-colors"
        >
          {showFullSummary ? 'Read Less' : 'Read More'}
          {showFullSummary ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </button>
      )}
    </div>
  );
};

const EpisodeItem = ({ episode, bookData, onEpisodeClick }) => (
  <div 
    key={episode.episode_id}
    onClick={() => onEpisodeClick(episode.episode_id)}
    className="flex items-center justify-between py-4 border-b border-gray-700 cursor-pointer hover:bg-gray-800 transition-colors rounded px-2 -mx-2"
  >
    <div className="flex items-center flex-1">
      <div className="flex-1">
        <p className="text-white text-sm font-medium">{episode.episode_name}</p>
        <div className="flex items-center mt-1">
          <Clock className="w-4 h-4 text-gray-400 mr-1" />
          <span className="text-gray-400 text-xs">{episode.episode_duration}</span>
        </div>
      </div>
    </div>
    
    <div className="ml-4 flex items-center">
    {/* || bookData.already_buy === 1 */}
      {episode.episode_type === 'free'  ? (
        <div className="flex items-center">
          {episode.episode_type === 'free' && (
            <span className="bg-yellow-500 text-white w-8 h-8 rounded-full text-xs font-bold mr-2 flex items-center justify-center">
              Free
            </span>
          )}
        </div>
      ) : (
        <Lock className="w-5 h-5 text-gray-500" />
      )}
    </div>
  </div>
);

const ChaptersList = ({ bookData, onEpisodeClick, chaptersRef }) => {
  const [showChapters, setShowChapters] = useState(true);

  if (!bookData.episodes || bookData.episodes.length === 0) return null;

  return (
    <div ref={chaptersRef} className="mb-20">
      <button 
        onClick={() => setShowChapters(!showChapters)}
        className="flex items-center justify-between w-full mb-4 hover:opacity-80 transition-opacity"
      >
        <h2 className="text-yellow-400 text-lg font-bold">Chapters</h2>
        {showChapters ? (
          <ChevronUp className="w-5 h-5 text-yellow-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-yellow-400" />
        )}
      </button>
      
      {showChapters && (
        <div className="space-y-0">
          {bookData.episodes.map((episode) => (
            <EpisodeItem
              key={episode.episode_id}
              episode={episode}
              bookData={bookData}
              onEpisodeClick={onEpisodeClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
const BookBySeriesOld = ({ bookData }) => {
  const chaptersRef = useRef(null);
  const { handleListen, handleBookClick, handleEpisodeClick, loading: actionLoading } = useBookActions(bookData);
  const {
    showConfirm,
    errorMessage,
    successMessage,
    loading: purchaseLoading,
    handlePurchase,
    confirmPurchase,
    cancelPurchase,
    navigateToCreditFill,
    handleSuccessPurchase,
  } = usePurchase(bookData);

  const isLoading = actionLoading || purchaseLoading;

  const handleListenClick = () => {
    handleListen(chaptersRef);
  };

  console.log("Book Data", bookData);

  return (
    <Box className="w-full mt-2">
      {isLoading && <LoadingSpinner />}

      <div className="bg-gray-900 text-white min-h-screen max-w-md mx-auto">
        <BookCover bookData={bookData} />
        
        <div className="px-4">
          <BookInfo bookData={bookData} />
          
          <ActionButtons
            bookData={bookData}
            onListen={handleListenClick}
            onPurchase={handlePurchase}
            onBookClick={handleBookClick}
            loading={isLoading}
          />

          <BookStats bookData={bookData} />
          <Summary bookData={bookData} />
          
          <ChaptersList
            bookData={bookData}
            onEpisodeClick={handleEpisodeClick}
            chaptersRef={chaptersRef}
          />
        </div>
      </div>

      {/* Modals */}
      <Modal isOpen={showConfirm}>
        <ConfirmationModal
          message="Are you sure to purchase this series?"
          onConfirm={confirmPurchase}
          onCancel={cancelPurchase}
        />
      </Modal>

      <Modal isOpen={!!errorMessage}>
        <MessageModal
          message={errorMessage}
          onCancel={cancelPurchase}
          onConfirm={navigateToCreditFill}
          confirmText="OK"
          cancelText="Cancel"
        />
      </Modal>

      <Modal isOpen={!!successMessage}>
        <MessageModal
          message={successMessage}
          onCancel={cancelPurchase}
          onConfirm={handleSuccessPurchase}
          confirmText="OK"
          cancelText="Cancel"
        />
      </Modal>
    </Box>
  );
};

export default BookBySeriesOld;